import React from 'react';
import { Box, Typography, styled } from '@mui/material';
import MobileMenuButton from './MobileMenuButton';

/*
  PageHeader: the single page-title header used across all pages.
  (SectionHeader delegates here so existing pages share this implementation.)

  Props:
   - title: string (required)
   - subtitle: string | node (optional)
   - icon: React node (optional, placed before title)
   - actions: React node (optional, right side content e.g. buttons, tabs)
   - sx: MUI sx overrides
   - disableMinHeight: boolean, for a shorter header
   - children: rendered below the main header row (e.g. extra filter bars)

  On mobile (md-down) the header hosts the nav drawer toggle on its left via
  MobileMenuButton, which reads the handler from LayoutContext.
*/

const HeaderRoot = styled(Box)(({ theme }) => ({
  background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  boxShadow: theme.shadows[2],
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: 80,
  // Below md the header goes full-bleed: square corners and negative side
  // margins that cancel the main content gutters set in App.js (px: 1 on xs,
  // 2 on sm) — keep the two in sync.
  [theme.breakpoints.only('xs')]: {
    borderRadius: 0,
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
  },
  [theme.breakpoints.only('sm')]: {
    borderRadius: 0,
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
}));

const Row = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: 8,
}));

export default function PageHeader({ title, subtitle, icon, actions, children, sx, disableMinHeight }) {
  return (
    <HeaderRoot sx={{ mb: 2, minHeight: disableMinHeight ? 'auto' : 80, ...sx }}>
      <Row>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <MobileMenuButton />
          {icon && (
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'inherit', opacity: 0.95 }}>
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h1" sx={{ color: '#fff', lineHeight: 1.1 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25, color: '#fff' }} noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {actions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {actions}
          </Box>
        )}
      </Row>
      {children && <Box sx={{ mt: 1 }}>{children}</Box>}
    </HeaderRoot>
  );
}
